import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Container, Grid, Divider } from "@material-ui/core";

import HeaderClientes from "components/_refactor/headers/clients";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import StructureFields from "components/_refactor/fields/structure";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import FilterCard from "components/_refactor/filter-card";
import LoadingData from "components/Loading/LoadingData";
import TableVitrinesEnviadas from "./components/table-vitrines-enviadas";
import DialogDetailShowcase from "../minhas-vitrines/components/dialog-detail-showcase";

import Api from "lib/api/httpRequestHelper";
import MaskAndValid from "utils/maskAndValid";
import UtilsFncs from "utils/UtilsFncs";
import { IsMobile900, IsMobile } from "utils/is-mobile";
import InputText from "components/_refactor/text/input";

const STATUS_OPTIONS = [
  { value: "ABERTO", name: "Aberto", id: 1 },
  { value: "CONFIRMADO", name: "Confirmado", id: 2 },
  { value: "FINALIZADO", name: "Finalizado", id: 3 },
  { value: "PENDENTE", name: "Pendente", id: 4 },
  { value: "", name: "Todos", id: 5 },
];

const INITIAL_FILTERS = {
  status: STATUS_OPTIONS[4],
  structure: {},
  cpf: "",
  seller: "",
};

export default function VitrinesEnviadas() {
  const location = useLocation();
  const snackbar = useSnackbar();

  const isMobile900 = IsMobile900();
  const isMobile = IsMobile();
  const itemXS = isMobile ? 12 : isMobile900 ? 6 : 3;

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [sellerList, setSellerList] = useState([]);
  const [searchingSeller, setSearchingSeller] = useState(false);
  const [structureLoading, setStructureLoading] = useState([]);
  const [clearFilters, setClearFilters] = useState(false);

  const [result, setResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const [pagination, setPagination] = useState({
    totalRegisters: 0,
    currentPage: 1,
    pageSize: 25,
  });

  const [dialogs, setDialogs] = useState({
    detail: false,
    selectedShowcase: null,
  });

  const [idVitrineConsumidor, setIdVitrineConsumidor] = useState(null);

  const handleGetSeller = useCallback(async () => {
    if (!filters.structure?.idLoja) return;

    setSearchingSeller(true);
    try {
      const response = await Api.get(
        "store-saller",
        "commercialstructure",
        `?idPessoaLoja=${filters.structure.idLoja}`,
        true
      );
      setSellerList(response?.Result?.ListaVendedores || []);
      setFilters((prev) => ({ ...prev, seller: "" }));
    } catch (error) {
      console.error(error);    } finally {
      setSearchingSeller(false);
    }
  }, [filters.structure?.idLoja]);

  const getShowcaseList = useCallback(
    async ({ page = 1, sizePerPage = 25 }) => {
      setCancelLoading(false);
      setSearching(true);

      try {
        const params = new URLSearchParams({
          statusVitrine: filters.status?.value || "",
          idPessoaLoja: filters.structure?.idLoja || "",
          cpf: filters.cpf || "",
          idVendedor: filters.seller?.IdLojaVendedor || "",
          page: page - 1,
          pageSize: sizePerPage,
          idVitrineVirtualConsumidor: idVitrineConsumidor || "",
        });

        const response = await Api.get(
          "virtual-customer-list",
          "showcase",
          `?${params.toString()}`,
          true
        );

        const { Result } = response;
        setResult(Result?.ListaVitrines || []);
        setPagination({
          totalRegisters: Result?.TotalRegistro || 0,
          currentPage: page,
          pageSize: sizePerPage,
        });
        setIdVitrineConsumidor(null);
      } catch (error) {
        console.error(error);
        snackbar.showMessage(error, { severity: "error" });      } finally {
        setSearching(false);
      }
    },
    [filters, idVitrineConsumidor, snackbar]
  );

  const handleSearch = useCallback(() => {
    setResult(null);
    getShowcaseList({
      page: 1,
      sizePerPage: pagination.pageSize,
    });
  }, [getShowcaseList, pagination.pageSize]);

  const handleClearFilters = () => {
    setClearFilters(true);
    setSearching(true);
    setFilters(INITIAL_FILTERS);
  };

  const handleStopSearching = () => {
    setCancelLoading(true);
    setSearching(false);
    setResult(null);
  };

  useEffect(() => {
    if (idVitrineConsumidor) {
      getShowcaseList({
        page: 1,
        sizePerPage: pagination.pageSize,
      });
    }
  }, [idVitrineConsumidor, getShowcaseList, pagination.pageSize]);

  useEffect(() => {
    const params = UtilsFncs.GetUrlSearchParams(location.search);
    if (params.idVitrineVirtualConsumidor) {
      setIdVitrineConsumidor(params.idVitrineVirtualConsumidor);
    }
  }, [location.search]);

  useEffect(() => {
    if (filters.structure?.idLoja) {
      handleGetSeller();
    }
  }, [filters.structure?.idLoja, handleGetSeller]);

  useEffect(() => {
    if (searching && clearFilters) {
      setSearching(false);
      setClearFilters(false);
    }
  }, [searching, clearFilters]);

  return (
    <>
      <HeaderClientes section="Vitrines Enviadas" subsection="Vendas" />
      <Container maxWidth={false} style={{ marginTop: "-4.5rem" }}>
        <FilterCard
          handleSearch={handleSearch}
          disabled={searching || structureLoading.length > 0 || searchingSeller}
          searching={searching}
          handleClearFilters={handleClearFilters}
        >
          <StructureFields
            clearFilters={clearFilters}
            onStructureFieldsChange={useCallback((structure) => {
              setFilters((prev) => {
                if (
                  prev.structure &&
                  structure &&
                  JSON.stringify(prev.structure) === JSON.stringify(structure)
                ) {
                  return prev;
                }
                return { ...prev, structure };
              });
            }, [])}
            disableAll={
              searching || structureLoading.length > 0 || searchingSeller
            }
            onStructureFieldsLoad={setStructureLoading}
            fieldsOrder={["store"]}
            hideFields={[
              "channel",
              "representative",
              "economicGroup",
              "storeSingle",
            ]}
          />

          <Grid item xs={12}>
            <Divider style={{ margin: "0.5rem 0 2rem" }} />
          </Grid>

          <Grid item xs={itemXS}>
            <InputText
              label="CPF Consumidor"
              value={filters.cpf}
              placeholder="000.000.000-00"
              onChange={(e) => {
                setFilters((prev) => ({
                  ...prev,
                  cpf: MaskAndValid.maskCPF(e.target.value.slice(0, 14)),
                }));
              }}
              disable={searching}
            />
          </Grid>

          <Grid item xs={itemXS}>
            <AutoCompleteField
              label="Vendedor"
              onChange={(newValue) => {
                setFilters((prev) => ({
                  ...prev,
                  seller: newValue !== "all" ? newValue : "",
                }));
              }}
              loading={searchingSeller}
              value={filters.seller}
              options={sellerList}
              disabled={searchingSeller || sellerList.length === 0 || searching}
              optionIdKey="IdLojaVendedor"
            />
          </Grid>

          <Grid item xs={itemXS}>
            <AutoCompleteField
              label="Status da Vitrine"
              onChange={(newValue) => {
                setFilters((prev) => ({
                  ...prev,
                  status: newValue !== "all" ? newValue : STATUS_OPTIONS[4],
                }));
              }}
              value={filters.status}
              options={STATUS_OPTIONS}
              disabled={searching}
              loading={clearFilters}
              optionLabelKey="name"
              optionIdKey="id"
            />
          </Grid>
        </FilterCard>

        <LoadingData
          searching={searching}
          onClick={handleStopSearching}
          label="Buscando dados..."
        />

        {result && !cancelLoading && !searching && (
          <TableVitrinesEnviadas
            dataTable={result}
            totalRegistros={pagination.totalRegisters}
            currentPage={pagination.currentPage}
            currentSizePerPage={pagination.pageSize}
            search={getShowcaseList}
            setOpenDialogDetail={(showcase) =>
              setDialogs({ detail: true, selectedShowcase: showcase })
            }
          />
        )}

        {dialogs.detail && (
          <DialogDetailShowcase
            type="historic"
            data={dialogs.selectedShowcase}
            handleClose={() =>
              setDialogs({ detail: false, selectedShowcase: null })
            }
          />
        )}
      </Container>
    </>
  );
}
